import { createPortal } from 'react-dom'
import { useIsMobile } from '../hooks/useIsMobile'

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  isDestructive?: boolean
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
}: ConfirmDialogProps) {
  const isMobile = useIsMobile()

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  if (!isOpen) return null

  // Both desktop and mobile render via portal at z-[110] to layer above fullscreen overlays (z-[100])
  const dialog = isMobile ? (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[110]"
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-white/10 rounded-t-2xl z-[111] animate-in slide-in-from-bottom duration-200">
        {/* Drag handle */}
        <div className="flex justify-center py-2">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          <h3 className="text-lg font-semibold text-[#EEEEEE] mb-2">{title}</h3>
          <p className="text-[#EEEEEE]/80 text-sm mb-3">{message}</p>
          {isDestructive && (
            <p className="text-xs text-red-400/80 mb-4">This action cannot be undone.</p>
          )}

          {/* Button stack (confirm first, cancel below) */}
          <div className="space-y-2">
            <button
              onClick={handleConfirm}
              className={`w-full py-3 text-sm font-medium rounded-lg transition-all min-h-[48px] ${
                isDestructive
                  ? 'bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 border border-red-500/30 text-red-400'
                  : 'bg-[#5E6AD2] hover:bg-[#6B78E0] active:bg-[#5E6AD2]/80 text-white'
              }`}
            >
              {confirmText}
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/10 text-zinc-200 text-sm font-medium rounded-lg transition-all min-h-[48px]"
            >
              {cancelText}
            </button>
          </div>
        </div>

        {/* Safe area padding for home indicator */}
        <div className="h-6" />
      </div>
    </>
  ) : (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center animate-in fade-in duration-200"
        onClick={onClose}
      >
        {/* Dialog */}
        <div
          className="w-full max-w-sm bg-[#111111] border border-white/5 rounded-xl shadow-2xl m-4 animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 pt-4 pb-3 border-b border-white/5">
            <h3 className="text-sm font-semibold text-white/90">{title}</h3>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-[#EEEEEE]/80 text-sm">{message}</p>
            {isDestructive && (
              <p className="text-xs text-red-400/80">This action cannot be undone.</p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-[#EEEEEE] text-sm rounded-lg transition-all"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 text-sm rounded-lg transition-all ${
                  isDestructive
                    ? 'bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 hover:border-red-500/50 text-red-400'
                    : 'bg-[#5E6AD2] hover:bg-[#6B78E0] text-white shadow-[0_0_20px_rgba(94,106,210,0.15)] hover:shadow-[0_0_30px_rgba(94,106,210,0.25)]'
                }`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(dialog, document.body)
}
